import { isGranted } from '@pcg/auth';
import { registerErrorMiddleware, useErrorStore } from '@support/core';

import AccessDeniedPage from '../pages/AccessDeniedPage.vue';
import NotFoundPage from '../pages/NotFoundPage.vue';
import ServerErrorPage from '../pages/ServerErrorPage.vue';
import { createRouter, createWebHistory } from 'vue-router';
import { authMiddleware } from '../middlewares/auth';
import { permissionMiddleware } from '../middlewares/permission';
import { useAuthStore } from '@/stores/auth';
import type { Permission } from '@support/permissions';

/**
 * Where `/` sends a signed-in user: the first section they can actually open.
 *
 * It used to send everyone to the inbox, which was correct while every account
 * that could sign in was a support agent. Since #773 split the desk (AGENT) from
 * instance administration (MANAGER), a manager holds no ticket grant at all —
 * and a hard redirect to `/tickets` would greet them with Access Denied on the
 * way in from the login page.
 */
const HOME_TARGETS: { name: string; permission: Permission }[] = [
  { name: 'Tickets', permission: 'st:core:tickets:list' },
  { name: 'Users', permission: 'st:core:users:create' },
  { name: 'Organizations', permission: 'st:core:organizations:create' },
];

const resolveHomeRoute = () => {
  const { user } = useAuthStore();

  const target = user
    ? HOME_TARGETS.find(({ permission }) => isGranted(user, permission))
    : undefined;

  // Every account can edit its own profile, so there is always somewhere to
  // land — a blank screen would be the one outcome worse than the wrong page.
  return { name: target?.name ?? 'EditMe' };
};

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior: () => ({
    top: 0,
  }),
  routes: [
    {
      path: '/',
      name: 'Home',
      component: { template: '<div />' },
      beforeEnter: () => resolveHomeRoute(),
      meta: {
        layout: 'main',
      },
    },
    {
      path: '/access-denied',
      name: 'AccessDenied',
      component: AccessDeniedPage,
      meta: {
        layout: 'empty',
        public: true,
      },
    },
    {
      path: '/server-error',
      name: 'ServerError',
      component: ServerErrorPage,
      meta: {
        layout: 'empty',
        public: true,
      },
    },
    // Catch-all 404 route - must be registered last
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: NotFoundPage,
      meta: {
        layout: 'main',
      },
    },
  ],
});

// Clear stale errors first, before any guards that may set new ones
registerErrorMiddleware(router);
router.beforeEach(authMiddleware);
// Runs after authMiddleware, which is what loads the user this reads. Until
// #773 it was commented out, which made every route's `authorize` meta dead
// code: the API still refused the calls, so nothing leaked, but any signed-in
// account could open /users by typing the URL and meet an empty admin page.
router.beforeEach(permissionMiddleware);

// Handle chunk loading errors after rebuild
router.onError((error) => {
  if (
    error.message.includes('Failed to fetch dynamically imported module')
    || error.message.includes('Importing a module script failed')
  ) {
    const errorStore = useErrorStore();
    errorStore.setError({
      statusCode: 418,
      title: 'Application Updated',
      description:
        'A new version of the application has been released. Please reload the page to get the latest updates.',
      critical: true,
    });
  }
});

export default router;
